import { EventEmitter, Injectable, Inject, Optional, RendererFactory2 } from '@angular/core';
import { ComponentLoader } from 'ngx-bootstrap/component-loader';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AbstractPanelService } from '@app/shared/panels/services/abstraction.panel.service';
import { PANEL_CONFIG_DEFAULT_OVERRIDE, PanelConfigDefaults, PanelOptionsClass } from '@app/shared/panels/services/option.class';
import { EmailModalRender } from '@app/shared/panels/email-modal-render/email-modal-render';
import { EmailModal } from '@app/shared/panels/renders/email-modal/email-modal';

export interface EmailModalFromAddress {
  id?: string;
  description?: string;
  name?: string;
}

export interface EmailModalAttachment {
  id?: string | number;
  name: string;
  size: number;
  url?: string;
  uploading?: boolean;
  progress?: number;
  error?: string;
  /** Existing/system attachments (e.g. a linked document) that can't be removed. */
  removable?: boolean;
}

export interface EmailModalActivityBanner {
  message: string;
  checked?: boolean;
}

export interface EmailModalSendPayload {
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string;
  body: string;
  attachments: EmailModalAttachment[];
  activityCompleted?: boolean;
}

export interface IEmailModalInitialState {
  fromAddresses: EmailModalFromAddress[];
  selectedFromAddress?: string;
  toAddresses?: string[];
  ccAddresses?: string[];
  subject?: string;
  body?: string;
  activityBanner?: EmailModalActivityBanner;
  /** Endpoint FileUploadService.uploadFile() posts each attachment to — varies by host screen/type. */
  attachmentUploadUrl?: string;
  existingAttachments?: EmailModalAttachment[];
  /** Populates the attachment-picker popup with the record's existing server-side files. */
  attachments_url?: string;
  /** Populates the "To"/"Cc" fields' suggested-recipient dropdown. */
  recipient_addresses_url?: string;
  /** Passed through to ApiService.improveTextAI() as the AI rewrite field identifier. */
  aiFieldName?: string;
}

export const defaultEmailModalConfig: Partial<IEmailModalInitialState> = {
  fromAddresses: [],
  toAddresses: [],
  ccAddresses: [],
  subject: '',
  body: '',
  aiFieldName: 'email_body',
};

let currentId = 2001;

@Injectable({
  providedIn: 'root',
})
export class EmailModalService extends AbstractPanelService {
  protected override loaders: ComponentLoader<any>[] = [];

  constructor(
    rendererFactory: RendererFactory2,
    @Optional() @Inject(PANEL_CONFIG_DEFAULT_OVERRIDE) protected override modalDefaultOption?: PanelOptionsClass,
  ) {
    super(rendererFactory, modalDefaultOption);
  }

  show(initialState: IEmailModalInitialState): { onSend: Subject<EmailModalSendPayload>; onClose: Subject<void> } | null {
    if (this.loaders.length) {
      return null;
    }
    const mergedInitialState = Object.assign({}, defaultEmailModalConfig, initialState);
    this.modalsCount = 1;
    const id = currentId++;
    this.lastHiddenId = null;
    this._createLoaders();
    this.config = Object.assign(
      {},
      PanelConfigDefaults,
      { appendTo: 'body', panelType: 'email-modal' },
      this.modalDefaultOption,
      { initialState: mergedInitialState },
    );
    this.config.id = id;
    this.lastDismissReason = void 0;

    const bsModalRef = this._showModal(EmailModal);
    if (bsModalRef instanceof BsModalRef) {
      return { onSend: bsModalRef.content.onSend, onClose: bsModalRef.content.onClose };
    }
    return null;
  }

  _showModal(content: any): BsModalRef | boolean {
    const modalLoader = this.loaders[this.loaders.length - 1];
    const bsModalRef = new BsModalRef<any>();
    const modalContainerRef = modalLoader
      .provide({ provide: PanelOptionsClass, useValue: this.config })
      .provide({ provide: BsModalRef, useValue: bsModalRef })
      .attach(EmailModalRender)
      .to(this.config.appendTo);
    bsModalRef.hide = () => modalContainerRef.instance?.hide();
    bsModalRef.onHidden = new EventEmitter<unknown>();
    bsModalRef.onHide = new EventEmitter<unknown>();
    this.copyEvent(modalLoader.onBeforeHide, bsModalRef.onHide);
    this.copyEvent(modalLoader.onHidden, bsModalRef.onHidden);

    modalContainerRef.show({
      content,
      isAnimated: this.config.animated,
      initialState: this.config.initialState,
      bsModalService: this,
      id: this.config.id,
    });
    if (modalContainerRef.instance) {
      modalContainerRef.instance.level = this.getModalsCount();
      bsModalRef.content = modalLoader.getInnerComponent();
      bsModalRef.id = modalContainerRef.instance.config?.id;
    }
    return bsModalRef;
  }
}
